import React from 'react';
import { OLoading } from '../../static/svg/icons';
import { useQuery } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { getAxiosInstance } from '../../utils/axiosInstance';
import toast, { Toaster } from 'react-hot-toast';
import Button from '../../NecttosComp/Button/Button';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const getForm = async ({ queryKey }) => {
	const [_key, responseId] = queryKey;
	try {
		const instance = getAxiosInstance();
		const { data, status } = await instance.get('/college/forms/getFormResponse', {
			params: { responseId },
		});
		if (status === 200) return data;
		throw new Error(data?.message || 'Failed to fetch form details.');
	} catch (error) {
		if (error.response?.status === 404) {
			toast.error('Form not found');
			return {
				acceptResponse: false,
				formData: [],
				title: 'Form not found',
				description: 'The form you are looking for does not exist.',
				error: true,
			};
		}
		toast.error('Failed to fetch form details. Please try again later.');
		return { error: true };
	}
};

const Response = () => {
	const { responseId } = useParams();
	const { data, isLoading, error } = useQuery(['responseId', responseId], getForm);
	const printRef = React.useRef(null);
	const history = useHistory();

	const handleDownloadPdf = async () => {
		if (!printRef.current) return;
		try {
			const canvas = await html2canvas(printRef.current, {
				scale: 2,
				useCORS: true,
				scrollY: -window.scrollY,
			});
			const pdf = new jsPDF({
				orientation: 'portrait',
				unit: 'px',
				format: [canvas.width, canvas.height],
			});
			pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, canvas.width, canvas.height);
			pdf.save(`form_response_${responseId}.pdf`);
		} catch (err) {
			console.error(err);
			toast.error('Failed to generate PDF. Please try again.');
		}
	};

	const handleEdit = () => {
		history.replace(`/forms/${data?.form?.formId}`, data?.response);
	};

	if (isLoading) {
		return (
			<div className='flex items-center justify-center min-h-[60vh] bg-white'>
				<OLoading styles={{ height: 30, width: 30 }} />
			</div>
		);
	}

	if (error || data?.error) {
		return (
			<div className='min-h-screen flex items-center justify-center bg-white'>
				<Toaster />
				<div className='text-center text-red-600'>
					<h1 className='text-2xl font-bold mb-2'>Error</h1>
					<p>We couldn't fetch the form response. Please check the link or try again later.</p>
				</div>
			</div>
		);
	}

	const { response, form } = data;
	console.log({ form });

	return (
		<div className='min-h-screen bg-white py-6 px-4 overflow-y-auto text-black'>
			<Toaster />
			<div className='flex justify-center gap-4 mb-4 text-sm no-print'>
				<Button
					type='call'
					onClick={() => {
						navigator.clipboard.writeText(`https://necttos.com/response/${responseId}`);
						toast.success('Copied');
					}}
					className='text-blue-600 hover:underline'>
					📋 Copy
				</Button>
				<Button
					type='complete'
					onClick={() =>
						navigator.share?.({
							title: form?.title,
							text: form?.description,
							url: `https://necttos.com/response/${responseId}`,
						})
					}
					className='text-green-600 hover:underline'>
					🔗 Share
				</Button>
				<Button type='edit' onClick={handleEdit} className='text-purple-600 hover:underline'>
					✏️ Edit
				</Button>
				<Button type='download' onClick={handleDownloadPdf} className='text-orange-600 hover:underline'>
					📥 Download PDF
				</Button>
			</div>

			<div className='bg-white rounded-2xl shadow-xl  print:shadow-none print:rounded-none print:p-0 print:px-4 printable' id='print' ref={printRef}>
				<header className='flex flex-col md:flex-row items-start md:items-center gap-6 border-b pb-6'>
					{!form?.error ? (
						<div className='flex-shrink-0'>
							<img src={form?.logo} alt={`${form?.collegeName} Logo`} className='w-24 h-24 object-contain rounded-lg border border-gray-300' />
						</div>
					) : (
						<h1 className='text-red-500 text-3xl font-bold'>404</h1>
					)}

					<div className='w-full space-y-1'>
						<h1 className='text-2xl font-extrabold text-gray-900 leading-tight'>{form?.collegeName}</h1>
						<p className='text-base text-gray-700'>{form?.address}</p>
						<p className='text-sm font-medium text-indigo-600'>{form?.accreditation}</p>

						{form?.website && (
							<a href={form.website.startsWith('http') ? form.website : `https://${form.website}`} target='_blank' rel='noopener noreferrer' className='text-sm text-blue-600 hover:underline block'>
								🌐 {form.website}
							</a>
						)}

						<div className='pt-2 border-t mt-2'>
							<h2 className='text-xl font-bold text-gray-800 uppercase tracking-wide'>Management Quota Application</h2>
							<p className='text-sm text-gray-600 font-medium'>{form?.title}</p>
						</div>
					</div>
				</header>

				<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'>
					{form?.formData?.map((item) => {
						let answer = response[item._id];

						if (answer?.toLowerCase?.().includes('other')) {
							answer = response[`${item._id}_other`] || answer;
						}

						const isArray = Array.isArray(answer);
						let answerText = isArray ? answer.join(', ') : typeof answer === 'object' && answer !== null ? JSON.stringify(answer) : answer;

						const combinedLength = (item?.name?.length || 0) + (answerText?.length || 0);

						let colSpanClass = 'col-span-1';
						if (combinedLength > 50) colSpanClass = 'col-span-full';
						else if (combinedLength > 35) colSpanClass = 'sm:col-span-2';

						return (
							<div key={item._id} className={`flex items-start gap-2 border p-1 rounded bg-gray-50 text-sm ${colSpanClass}`}>
								<span className='font-semibold text-gray-800 whitespace-nowrap min-w-[80px]'>{item.name} :</span>
								<span className='text-gray-700'>{answerText || <i style={{ color: '#888' }}>Not filled</i>}</span>
							</div>
						);
					})}
				</div>

				{response?.applicationAmount?.recieved && (
					<div className=' p-2 bg-gray-100 rounded-lg print:bg-transparent print:p-0 border border-gray-200'>
						<h3 className='text-lg font-bold text-gray-800 mb-3 border-b '>💳 Payment Details</h3>

						<div className='flex flex-wrap text-sm text-gray-700'>
							<div className='w-full sm:w-1/2 pr-4 mb-1'>
								<span className='block font-medium text-gray-600'>Payment ID</span>
								<span>{response?.applicationAmount?.receipt || 'Not provided'}</span>
							</div>
							<div className='w-1/2 sm:w-1/4 pr-4 mb-1'>
								<span className='block font-medium text-gray-600'>Amount</span>
								<span>₹{response?.applicationAmount?.totalAmount || 'Not provided'}</span>
							</div>
							<div className='w-1/2 sm:w-1/4 mb-1'>
								<span className='block font-medium text-gray-600'>Status</span>
								<span className={`font-semibold ${response?.paymentStatus?.toLowerCase() === 'paid' ? 'text-green-600' : 'text-red-600'}`}>{response?.paymentStatus || 'Paid'}</span>
							</div>
						</div>
					</div>
				)}
				<div className='mt-2 '>
					<h3 className='text-lg font-bold text-gray-800 mb-2 border-b pb-2'>🖊️ Signatures</h3>

					<div className='flex justify-between gap-4 text-sm text-gray-800 mt-2'>
						<div className='flex flex-col items-center w-1/3'>
							<div className='h-20 border-b border-gray-400 w-full'></div>
							<span className='mt-2 font-medium text-center'>Recommended by</span>
						</div>

						<div className='flex flex-col items-center w-1/3'>
							<div className='h-20 border-b border-gray-400 w-full'></div>
							<span className='mt-2 font-medium text-center'>Signature of Student</span>
						</div>

						<div className='flex flex-col items-center w-1/3'>
							<div className='h-20 border-b border-gray-400 w-full'></div>
							<span className='mt-2 font-medium text-center'>Signature of Parent / Guardian</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Response;
